/**
 * Queue URL state — docs/spec/05 §2.
 *
 * The queue's filters live in the URL, not in a store: a transcriber sharing
 * "the QA backlog" with a lead should be able to paste a link and have them see
 * the same list.
 *
 * Everything here **validates and falls back**. The address bar is user input,
 * and the api punishes bad input harshly (02 §3): `?page=abc` is a 500 with an
 * empty message, `?page=0` is a 404, a bad `status` is a 422 whose message
 * echoes the invalid value as if it were the allowed set. None of that should
 * be reachable by mistyping a URL, so a nonsense value silently becomes the
 * default rather than being forwarded.
 */
import type { DateType, JobListFilters, SortDir } from '../../api/jobs'
import { isFilterableStatus } from '../../lib/jobStatus'

/**
 * The URL shape. Every field is optional so that a bare `/queue` is a valid,
 * bookmarkable URL and `<Link to="/queue">` needs no search argument —
 * defaults are applied when reading (`resolveQueueSearch`), not written into
 * the address bar on first load.
 */
export interface QueueSearch {
  readonly status?: readonly string[] | undefined
  readonly page?: number | undefined
  readonly assignee?: string | undefined
  readonly q?: string | undefined
  readonly dateType?: DateType | undefined
  readonly startDate?: string | undefined
  readonly endDate?: string | undefined
  readonly orderBy?: string | undefined
  readonly sortDir?: SortDir | undefined
}

/** The working shape: what the screen actually renders from. */
export interface ResolvedQueueSearch extends QueueSearch {
  readonly status: readonly string[]
  readonly page: number
  readonly orderBy: string
  readonly sortDir: SortDir
  readonly dateType: DateType
}

/** The queue opens on unclaimed work — that is the job to be done (04 §2). */
export const DEFAULT_STATUS: readonly string[] = ['open']
export const DEFAULT_PAGE = 1
export const PER_PAGE = 50

/**
 * Oldest first, matching legacy (`matSortActive` = the first column,
 * `matSortDirection="asc"` — `legacy:…/job-list/job-list.component.html`).
 *
 * This is a deliberate override of the api's own default, which is
 * `time_created` **desc** (02 §3). It matters: a work queue sorted newest-first
 * lets the oldest mail sink to the bottom and sit there, which is the opposite
 * of what a FIFO queue is for. Sending the sort explicitly on every request is
 * what keeps the api's default from quietly reversing the operators' worklist.
 */
export const DEFAULT_ORDER_BY = 'time_created'
export const DEFAULT_SORT_DIR: SortDir = 'asc'

/**
 * Which timestamp the date bounds apply to. `completed` matches both legacy's
 * form default and the api's own default (02 §3).
 *
 * Worth knowing: `completed_at` is NULL on an `open` job, so the DEFAULT status
 * filter combined with this DEFAULT date type matches nothing at all —
 * verified live, `status=open` with a 2020–2030 range returns 0. That is why
 * the control is surfaced as soon as a date is picked, and why the empty state
 * names this specific cause (04 §2).
 */
export const DEFAULT_DATE_TYPE: DateType = 'completed'

const DATE_TYPES: readonly string[] = ['created', 'updated', 'completed']
const SORT_DIRS: readonly string[] = ['asc', 'desc']

/**
 * Columns a user is allowed to sort by — exactly the four legacy offers
 * (`mat-sort-header` on Created, Completed, Parcel Code, Status).
 *
 * An allow-list rather than passing the value through, for two reasons:
 * `order_by` must be a real model column or the api 422s, so an arbitrary
 * string from the URL is a guaranteed error response; and every accepted value
 * must correspond to a header that can actually show the active sort, or the
 * table would be sorted in a way the user can neither see nor undo.
 *
 * Sender / Recipient / Transcriber are deliberately absent: they live on joined
 * tables, and legacy does not offer them either.
 */
export const SORTABLE_COLUMNS: readonly string[] = [
  'time_created',
  'completed_at',
  'parcel_code',
  'status',
]

/**
 * Accepts an array or a comma-joined string, since both appear in real URLs.
 * Returns undefined when nothing valid survives, so the caller can omit the
 * key entirely rather than writing the default into the URL.
 */
function parseStatus(raw: unknown): readonly string[] | undefined {
  const candidates: unknown[] = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(',')
      : []

  const valid = candidates
    .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
    .filter(isFilterableStatus)

  // De-duplicate: `?status=open,open` should be one filter, one cache entry.
  const unique = [...new Set(valid)]
  return unique.length > 0 ? unique : undefined
}

function parsePage(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined
  const value = typeof raw === 'number' ? raw : Number(raw)
  // Number('') is 0 and Number(null) is 0 — exactly the api's 404 case — so
  // the integer and lower-bound checks have to come after coercion.
  if (!Number.isInteger(value) || value < 1) return undefined
  return value
}

function parseText(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  return trimmed === '' ? undefined : trimmed
}

function parseEnum<T extends string>(raw: unknown, allowed: readonly string[]): T | undefined {
  if (typeof raw !== 'string') return undefined
  const value = raw.trim().toLowerCase()
  return allowed.includes(value) ? (value as T) : undefined
}

/**
 * The api parses bounds with Python's `datetime.fromisoformat`, so the shape
 * has to be **ISO-8601** — `YYYY-MM-DD`, optionally with a time.
 *
 * Deliberately stricter than `Date.parse`, which happily accepts US-style
 * `07/01/2026` that the api answers with a 422 "Invalid start date or end
 * date" (verified live). Matching the api's parser rather than JavaScript's is
 * the whole point of validating here.
 */
const ISO_DATE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

function parseDateBound(raw: unknown): string | undefined {
  const text = parseText(raw)
  if (text === undefined) return undefined
  if (!ISO_DATE.test(text)) return undefined
  // Shape alone is not enough: `2026-13-45` is ISO-shaped and still not a date.
  return Number.isNaN(Date.parse(text)) ? undefined : text
}

/**
 * Route-level `validateSearch`. Total: every input produces a usable state.
 *
 * **Every key this route owns is returned explicitly, `undefined` included.**
 * That is not stylistic — it is load-bearing, and the reason is not obvious:
 *
 * TanStack Router builds a match's search by merging the parent match's search
 * with this function's result. The queue's parents (`__root__` and the
 * pathless `authenticated` layout) have no validator, so *their* search is the
 * raw, unvalidated query string. A key this function **omits** is therefore not
 * rejected — the parent's raw value survives the merge and reaches the screen.
 *
 * Verified against @tanstack/react-router 1.170: for `/queue?page=abc`, a
 * validator returning `{}` yields `match.search === { page: 'abc' }`, while one
 * returning `{ page: undefined }` yields `{}`. With the omitting version the
 * app forwarded `?page=abc` to the api and got the 500 this validation exists
 * to prevent.
 *
 * Undefined values are dropped when the router serialises the search back to
 * the URL, so a clean `/queue` still stays clean.
 */
export function validateQueueSearch(search: Record<string, unknown>): QueueSearch {
  return {
    status: parseStatus(search['status']),
    page: parsePage(search['page']),
    assignee: parseText(search['assignee']),
    q: parseText(search['q']),
    dateType: parseEnum<DateType>(search['dateType'], DATE_TYPES),
    startDate: parseDateBound(search['startDate']),
    endDate: parseDateBound(search['endDate']),
    orderBy: parseEnum<string>(search['orderBy'], SORTABLE_COLUMNS),
    sortDir: parseEnum<SortDir>(search['sortDir'], SORT_DIRS),
  }
}

/** Fill in what the URL left unsaid. */
export function resolveQueueSearch(search: QueueSearch): ResolvedQueueSearch {
  return {
    ...search,
    status: search.status ?? DEFAULT_STATUS,
    page: search.page ?? DEFAULT_PAGE,
    orderBy: search.orderBy ?? DEFAULT_ORDER_BY,
    sortDir: search.sortDir ?? DEFAULT_SORT_DIR,
    dateType: search.dateType ?? DEFAULT_DATE_TYPE,
  }
}

/**
 * True when the current filters are empty **by construction** rather than by
 * accident — a date range applied to `completed_at` while every selected status
 * is one that has no completion time yet.
 *
 * Verified live: `status=open` with a 2020–2030 `completed` range returns 0 of
 * 1,790. Without naming this, an operator sees an empty queue and concludes the
 * app is broken; legacy gives them nothing to go on either.
 */
export function isImpossibleDateFilter(search: ResolvedQueueSearch): boolean {
  const hasBound = search.startDate !== undefined || search.endDate !== undefined
  if (!hasBound || search.dateType !== 'completed') return false
  // These statuses precede completion, so `completed_at` is null for them and
  // no range can ever match.
  const NEVER_COMPLETED = ['open', 'transcription_in_progress']
  return search.status.every((status) => NEVER_COMPLETED.includes(status))
}

/**
 * What clicking a column header should do next.
 *
 * Clicking the **active** column flips direction; clicking a different one
 * switches to it and starts ascending. There is deliberately no third
 * "unsorted" state — legacy sets `disableClear`, and a list that can drop back
 * to an arbitrary server-side order gives the user no way to reason about what
 * they are looking at.
 */
export function nextSort(
  current: ResolvedQueueSearch,
  column: string,
): { orderBy: string; sortDir: SortDir } {
  if (current.orderBy === column) {
    return { orderBy: column, sortDir: current.sortDir === 'asc' ? 'desc' : 'asc' }
  }
  return { orderBy: column, sortDir: 'asc' }
}

/**
 * URL state → api filters.
 *
 * The rename from `q` to `filterTerm` happens here: the URL uses the short,
 * conventional `?q=`, and the api wants `filter_term` (02 §3).
 */
export function searchToFilters(search: QueueSearch): JobListFilters {
  const resolved = resolveQueueSearch(search)
  return {
    page: resolved.page,
    perPage: PER_PAGE,
    status: resolved.status,
    ...(resolved.assignee !== undefined ? { assignee: resolved.assignee } : {}),
    ...(resolved.q !== undefined ? { filterTerm: resolved.q } : {}),
    // Sent explicitly for the same reason as the sort: the api's default is
    // also `completed`, but leaving it implicit means the behaviour is defined
    // somewhere this client cannot see.
    dateType: resolved.dateType,
    ...(resolved.startDate !== undefined ? { startDate: resolved.startDate } : {}),
    ...(resolved.endDate !== undefined ? { endDate: resolved.endDate } : {}),
    // Always sent, never conditional: leaving the sort off lets the api's own
    // `time_created` DESC default take over and silently reverse the worklist.
    orderBy: resolved.orderBy,
    sortDir: resolved.sortDir,
  }
}

/** True when the user has narrowed the queue beyond its default view. */
export function hasActiveFilters(search: QueueSearch): boolean {
  const status = search.status ?? DEFAULT_STATUS
  return (
    search.assignee !== undefined ||
    search.q !== undefined ||
    search.startDate !== undefined ||
    search.endDate !== undefined ||
    status.length !== DEFAULT_STATUS.length ||
    !status.every((value) => DEFAULT_STATUS.includes(value))
  )
}

/**
 * True when a queue search narrows nothing — the state a bare `/queue` produces.
 *
 * Checks VALUES, not key count. `validateQueueSearch` returns all nine keys
 * explicitly with `undefined` values (see its docblock — omitting a key lets the
 * parent match's raw value survive the merge), so this object is never key-less
 * and `Object.keys(search).length === 0` would never be true.
 *
 * An empty `status` array counts as empty: it filters nothing, and restoring it
 * over a default would be a no-op the operator cannot see. (04 §3c)
 */
export function isEmptyQueueSearch(search: QueueSearch): boolean {
  return Object.values(search).every(
    (value) => value === undefined || (Array.isArray(value) && value.length === 0),
  )
}
